import React from "react";
import ReactDOM from "react-dom";
import NumberFormat from "react-number-format";
import numeral from "numeral";

import moment from "moment";

import { Button, getRange } from "aible";

import ResponsiveChart from "components/RunPlot/ResponsiveChart";

import { AutoSizer } from "react-virtualized";

import {
  LineChart,
  Line,
  Scatter,
  CartesianGrid,
  ComposedChart,
  Area,
  CartesianAxis,
  Tooltip,
  Legend,
  XAxis,
  YAxis,
  ZAxis,
  ReferenceLine,
  ReferenceDot,
  ReferenceArea,
  ResponsiveContainer,
  ErrorBar,
  Label,
  Cell,
  LabelList
} from "recharts";

import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import ModelTileContainer from "containers/Tiles/ModelTile";

import { LineInput } from "components/Inputs/LineInput";

import styles from "./style.module.css";

const sortValues = {
  Trend: 0,
  Seasonal: 1,
  Other: 2,
  TrendPrediction: 3,
  SeasonalPrediction: 4,
  OtherPrediction: 5,
  undefined: 10
};

const CustomToolTip = (props) => {
  const { active, payload, label, setupLabels } = props;
  if (!active || !payload) {
    return null;
  }

  const renderRange = (rPayload) => {
    const values = rPayload && rPayload.value;
    if (!values || values.length !== 2) {
      return null;
    }

    return (
      <React.Fragment key={rPayload.dataKey}>
        <tr className={styles.tBody}>
          <td className={styles.tLabel} style={{ color: rPayload.color }}>
            {"Upper Bound: "}
          </td>
          <td>{numeral(values[1]).format("0.00")}</td>
        </tr>
        <tr className={styles.tBody}>
          <td className={styles.tLabel} style={{ color: rPayload.color }}>
            {"Lower Bound: "}
          </td>
          <td>{numeral(values[0]).format("0.00")}</td>
        </tr>
      </React.Fragment>
    );
  };

  const dPayload = payload.find((e) => e && e.payload && e.payload.Date);
  const date = dPayload && dPayload.payload && dPayload.payload.Date;

  const retPayload = payload
    .map((e) => e)
    .sort((a, b) => {
      const aValue = sortValues[a && a.dataKey];
      const bValue = sortValues[b && b.dataKey];
      return aValue - bValue;
    });

  return (
    <div className={styles.cTooltip}>
      <div className={styles.tHeader}>{date}</div>
      <table className={styles.tTable}>
        <tbody>
          {retPayload.map((p, i) => {
            if (
              !p ||
              (p.payload &&
                p.payload.hidePrediction &&
                !(p.dataKey === "Trend" || p.dataKey === "Seasonal" || p.dataKey === "Other"))
            ) {
              return null;
            }
            if (p.dataKey === "Range") {
              // return renderRange(p);
              return null;
            }
            let label;
            let color = p.color;
            switch (p.dataKey) {
              case "Trend":
                label = "Trend";
                break;
              case "TrendPrediction":
                label = "Predicted Trend";
                break;
              case "Seasonal":
                label = "Seasonal";
                break;
              case "SeasonalPrediction":
                label = "Predicted Seasonal";
                break;
              case "Other":
                label = "Other Factors";
                color = "#A4A6B7";
                break;
              case "OtherPrediction":
                label = "Other Factors";
                color = "#A4A6B7";
                break;
            }

            if (label === undefined) {
              return null;
            }

            return (
              <tr key={p.dataKey}>
                <td className={styles.tLabel} style={{ color: color }}>
                  {label + ": "}
                </td>
                <td>{(setupLabels.unitType || "") + numeral(p.value).format("0.00")}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const CustomLabel = (props) => {
  const { value, position, offset, viewBox } = props;

  let x, y;
  switch (position) {
    case "top":
      x = viewBox.x + offset;
      y = 0 + offset;
      break;
    case "bottom":
      x = viewBox.x + viewBox.width / 2;
      y = viewBox.y + viewBox.height + offset;
      break;
    default:
      x = viewBox.x + offset;
      y = viewBox.y + offset;
  }

  return (
    <text textAnchor="start" className={styles.aLabel}>
      <tspan x={x} y={y}>
        {value}
      </tspan>
    </text>
  );
};

class TrendChart extends React.PureComponent {
  renderLegend() {
    const {} = this.props;
    return (
      <div className={styles.forecastChartLegend}>
        <div className={styles.forecastChartLegendItem}>
          <div className={styles.forecastChartLine} />
          <div className={styles.forecastChartLabel}>{"Prediction"}</div>
        </div>
        <div className={styles.forecastChartLegendItem}>
          <div className={styles.forecastChartCircle} />
          <div className={styles.forecastChartLabel}>{"Likely Range"}</div>
        </div>
      </div>
    );
  }

  render() {
    const {
      models,
      model,
      forecastTrendPlot,
      fetchForecast,
      forecastTime,
      updateForecastTime,
      forecastTimeOptions,
      useForecastLocalRatio,
      forecastTimeUnit,
      updateForecastTimeUnit,
      setupLabels,
      forecastTimeCol,
      forecastChartDateRange
    } = this.props;

    if (!forecastTrendPlot) {
      return <React.Fragment />;
    }

    const retData = forecastTrendPlot.slice(forecastChartDateRange[0], forecastChartDateRange[1] + 1);

    let yMin = Infinity;
    let yMax = -Infinity;

    retData.forEach((d) => {
      if (!isNaN(d.Trend)) {
        yMin = Math.min(yMin, d.Trend);
        yMax = Math.max(yMax, d.Trend);
      }
      if (!isNaN(d.TrendPrediction)) {
        yMin = Math.min(yMin, d.TrendPrediction);
        yMax = Math.max(yMax, d.TrendPrediction);
      }
      if (!isNaN(d.Seasonal)) {
        yMin = Math.min(yMin, d.Seasonal);
        yMax = Math.max(yMax, d.Seasonal);
      }
      if (!isNaN(d.SeasonalPrediction)) {
        yMin = Math.min(yMin, d.SeasonalPrediction);
        yMax = Math.max(yMax, d.SeasonalPrediction);
      }
    });

    const yDomain = getRange(yMin, yMax, true, 1);

    return (
      <div className={styles.forecastChart} id="forecastPlot">
        <AutoSizer>
          {({ width, height }) => (
            <ComposedChart
              data={retData}
              margin={{
                top: 30,
                right: 20,
                left: 20,
                bottom: 40
              }}
              syncId="forecastCharts"
              width={width}
              height={height}
            >
              <XAxis dataKey="Date" name={"Date"}>
                <Label value={String(forecastTimeCol || "Date").toUpperCase()} offset={5} position="bottom" />
              </XAxis>
              <YAxis
                type="number"
                name={"Prediction"}
                domain={yDomain}
                tickFormatter={(tick) => setupLabels.unitType + numeral(tick).format("0.[00000]a")}
              >
                <Label value={String(setupLabels.numberPredicted).toUpperCase()} offset={15} position="top" />
              </YAxis>
              <CartesianGrid strokeDasharray="3" vertical={false} />
              <Line dataKey="Other" dot={false} stroke="#C8CAD4AA" strokeWidth={3} />
              <Line dataKey="Trend" dot={false} stroke="#7E69D7" strokeWidth={3} />
              <Line dataKey="Seasonal" dot={false} stroke="#7E69D7" strokeWidth={3} />
              <Line
                type="monotone"
                dataKey="OtherPrediction"
                dot={false}
                stroke="#C8CAD4AA"
                strokeWidth={3}
                strokeDasharray="5 5"
              />
              <Line
                type="monotone"
                dataKey="TrendPrediction"
                dot={false}
                stroke="#7E69D7"
                strokeWidth={3}
                strokeDasharray="5 5"
              />
              <Line
                type="monotone"
                dataKey="SeasonalPrediction"
                dot={false}
                stroke="#7E69D7"
                strokeWidth={3}
                strokeDasharray="5 5"
              />
              <Tooltip cursor={{ strokeDasharray: "3 3" }} content={<CustomToolTip setupLabels={setupLabels} />} />
            </ComposedChart>
          )}
        </AutoSizer>
      </div>
    );
  }
}

export default ResponsiveChart(TrendChart);
